import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AssessmentsAPI } from "@/lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import Comments from "@/components/Comments";
import DimensionCards, { getDimOrder } from "@/components/DimensionCards";
import RefreshComparison from "@/components/RefreshComparison";

function shortLabel(s) {
  if (!s) return "";
  return s.length > 22 ? s.slice(0, 20) + "…" : s;
}

function indexByKey(arr) {
  const m = {};
  (arr || []).forEach((d) => { m[d.key] = d; });
  return m;
}

function kindOverline(a) {
  if (a.assessment_kind === "system_level") return "System level";
  if (a.assessment_kind === "sub_premise") return `Sub-premise · ${a.sub_premise_name || ""}`;
  return "Primary assessment";
}

function sendBtnLabel({ sending, isProcessing }) {
  if (sending) return "Sending…";
  if (isProcessing) return "Wait for verdict";
  return "Send report →";
}

export default function AssessmentView() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const pollRef = useRef(null);

  const fetchOnce = useCallback(async (cur) => {
    try {
      const lite = cur && cur.status === "processing";
      const opts = { with_compare: 1 };
      if (lite) opts.lite = 1;
      const { data } = await AssessmentsAPI.get(id, opts);
      setA(data);
      return data;
    } catch {
      return null;
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const data = await fetchOnce(a);
      if (data && data.status === "processing") {
        pollRef.current = setTimeout(tick, 3000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // We intentionally do not depend on `a` — re-running on every refetch would create polling loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetchOnce]);

  const sendMail = async () => {
    setSending(true);
    setSent(null);
    try {
      const { data } = await AssessmentsAPI.emailReport({ assessment_id: id, to_email: emailTo, note });
      setSent(data);
    } catch (e) {
      setSent({ error: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  const dimByKey = useMemo(() => indexByKey(a?.dimensions), [a?.dimensions]);
  const cmpByKey = useMemo(() => indexByKey(a?.compare_against?.dimensions), [a?.compare_against?.dimensions]);
  // System-level audits emit a different set of 7 dimension keys than
  // pack audits — the DIM_ORDER for rendering has to be chosen from the
  // assessment kind, not hardcoded to the pack rubric.
  const dimOrder = useMemo(() => getDimOrder(a), [a]);

  if (!a) return <div className="v-container" style={{ paddingTop: 80 }}><div className="overline">Loading verdict…</div></div>;
  if (a.status === "failed") return <FailedView a={a} />;

  const isProcessing = a.status === "processing";
  const completedCount = a.dimensions?.length || 0;
  const radarData = dimOrder.map((od) => {
    const d = dimByKey[od.key];
    return { subject: od.short || shortLabel(od.label), score: d ? Number(d.score || 0) : 0 };
  });
  const heroOverline = kindOverline(a) + (isProcessing && completedCount > 0 ? ` · ${completedCount}/${dimOrder.length} in` : "");
  const scoreColor = isProcessing ? "var(--v-text-2)" : "var(--v-accent)";

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 96 }}>
      <Link to={`/projects/${a.project_id}`} className="overline" data-testid="back-to-project">← Project</Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" style={{ marginTop: 24 }}>
        <aside className="lg:col-span-5">
          <div>
            <div className="overline">{heroOverline}</div>
            <h1 className="display" style={{ fontSize: "clamp(36px,4.5vw,60px)", lineHeight: 1, marginTop: 10 }}>{a.title}</h1>
            <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 8, fontSize: 12 }}>
              by {a.designer_name} · {a.model_provider} / {a.model_name} · {new Date(a.created_at).toLocaleString()}
            </div>

            <div style={{ display: "flex", gap: 24, marginTop: 28, alignItems: "flex-end" }}>
              <div className="v-score" style={{ fontSize: 120, color: scoreColor }} data-testid="assessment-overall-score">
                {isProcessing ? <LiveDots /> : Number(a.overall_score || 0).toFixed(1)}
              </div>
              <div>
                <div className="mono" style={{ color: "var(--v-text-2)" }}>/ 10</div>
                <div className="overline" style={{ marginTop: 4 }}>{isProcessing ? "Live critique" : "Overall verdict"}</div>
              </div>
            </div>

            {isProcessing && (
              <div className="v-surface" style={{ padding: 14, marginTop: 16 }} data-testid="streaming-progress">
                <div className="overline">In progress · {a.progress?.current_label || "warming up"}</div>
                <div style={{ height: 4, marginTop: 10, background: "var(--v-border)" }}>
                  <div style={{ height: 4, background: "var(--v-accent)", width: `${(completedCount / dimOrder.length) * 100}%`, transition: "width .5s ease" }} />
                </div>
                <div className="mono" style={{ marginTop: 8, fontSize: 11, color: "var(--v-text-2)" }}>{completedCount} of {dimOrder.length} dimensions delivered</div>
              </div>
            )}

            {!isProcessing && (
              <div className="v-surface" style={{ padding: 16, marginTop: 16 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="var(--v-border)" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: "var(--v-text-2)", fontSize: 10 }} />
                    <PolarRadiusAxis domain={[0, 10]} tick={{ fill: "var(--v-text-2)", fontSize: 10 }} />
                    <Radar name="This pack" dataKey="score" stroke="var(--v-accent)" fill="var(--v-accent)" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            {!isProcessing && (
              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <a className="v-btn" href={AssessmentsAPI.exportUrl(id, "pdf")} target="_blank" rel="noreferrer" data-testid="export-pdf-btn">Export PDF</a>
                <a className="v-btn" href={AssessmentsAPI.exportUrl(id, "csv")} target="_blank" rel="noreferrer" data-testid="export-csv-btn">Export CSV</a>
                <Link
                  className="v-btn"
                  to={`/projects/${a.project_id}?duplicate=${id}`}
                  data-testid="duplicate-assessment-btn"
                  title="Copy this assessment's images + context into a new one, edit anything, and re-run."
                >
                  Duplicate & edit →
                </Link>
                {a.compare_against_assessment_id && (
                  <Link className="v-btn" to={`/assessments/${id}/diff`} data-testid="open-diff-btn">Side-by-side diff →</Link>
                )}
              </div>
            )}

            <div className="v-surface" style={{ padding: 20, marginTop: 24 }}>
              <div className="overline">Mail the verdict</div>
              <input className="v-input" type="email" placeholder="client@studio.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-to-input" style={{ marginTop: 10 }} />
              <textarea className="v-textarea" placeholder="Optional note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="email-note-input" style={{ marginTop: 10 }} />
              <button className="v-btn" onClick={sendMail} disabled={!emailTo || sending || isProcessing} data-testid="email-send-btn" style={{ marginTop: 12, width: "100%" }}>
                {sendBtnLabel({ sending, isProcessing })}
              </button>
              {sent && !sent.error && (
                <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11, marginTop: 8 }} data-testid="email-sent-status">
                  Queued · sent_via: {sent.sent_via}{sent.bcc_email ? ` · BCC: ${sent.bcc_email}` : ""}
                </div>
              )}
              {sent?.error && (
                <div className="mono" style={{ color: "var(--v-accent)", fontSize: 11, marginTop: 8 }} data-testid="email-error">{sent.error}</div>
              )}
            </div>
          </div>
        </aside>

        <main className="lg:col-span-7">
          {!isProcessing && (
            <div className="v-surface" style={{ padding: 28 }}>
              <div className="overline">Verdict summary</div>
              <p style={{ marginTop: 12, fontSize: 18, lineHeight: 1.55 }} data-testid="assessment-summary">{a.summary}</p>

              {a.recommendations?.length > 0 && (
                <>
                  <div className="overline" style={{ marginTop: 28 }}>Priority recommendations</div>
                  <ol style={{ marginTop: 8, paddingLeft: 18, lineHeight: 1.7 }} data-testid="assessment-recommendations">
                    {a.recommendations.map((r, i) => <li key={`rec-${i}-${typeof r === "string" ? r.slice(0, 32) : i}`}>{r}</li>)}
                  </ol>
                </>
              )}
            </div>
          )}

          <div style={{ marginTop: isProcessing ? 0 : 32 }}>
            <DimensionCards dimByKey={dimByKey} cmpByKey={cmpByKey} dimOrder={dimOrder} />
          </div>

          {!isProcessing && <RefreshComparison data={a.refresh_comparison} />}

          {!isProcessing && a.common_shortcomings_tags?.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <div className="overline">Shortcoming tags</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                {a.common_shortcomings_tags.map((t) => <span key={t} className="v-chip">{t}</span>)}
              </div>
            </div>
          )}

          {!isProcessing && (
            <div className="v-surface" style={{ padding: 28, marginTop: 32 }} data-testid="general-comments">
              <div className="overline">Discussion · general</div>
              <p style={{ marginTop: 4, fontSize: 13, color: "var(--v-text-2)" }}>Threaded notes between you, your collaborators and the studio lead. Per-dimension notes appear on each card above.</p>
              <div style={{ marginTop: 12 }}>
                <Comments assessmentId={id} />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function FailedView({ a }) {
  return (
    <div className="v-container" style={{ paddingTop: 80, paddingBottom: 96 }} data-testid="assessment-failed">
      <Link to={`/projects/${a.project_id}`} className="overline">← Project</Link>
      <div className="overline" style={{ marginTop: 24, color: "var(--v-accent)" }}>Assessment failed</div>
      <h1 className="display" style={{ fontSize: "clamp(36px,4.5vw,60px)", lineHeight: 1, marginTop: 12 }}>{a.title}</h1>
      <div className="v-surface" style={{ padding: 24, marginTop: 32 }}>
        <div className="overline">Reason</div>
        <p style={{ marginTop: 8, color: "var(--v-text-2)" }}>{a.error_message || "Unknown error during analysis."}</p>
      </div>
    </div>
  );
}

function LiveDots() {
  const [n, setN] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setN((x) => (x % 3) + 1), 450);
    return () => clearInterval(t);
  }, []);
  return <span>{".".repeat(n)}</span>;
}
